import React, { useEffect, useState, useCallback } from 'react';

import Dot from '@/assets/icons/dot.svg';
import IconPlaceholder from '@/assets/icons/icon-placeholder.svg';
import { useSwipe } from '@/hooks/use-swipe';

export interface CardCarouselProps<T> {
  items: T[];
  renderCard: (item: T, index: number) => React.ReactNode;
  renderSkeleton?: () => React.ReactNode;
  isLoading?: boolean;
  autoPlayInterval?: number; // ms, default 3000
  sliderNavClass?: string; // tailwind bottom-* class, default 'bottom-4'
  className?: string;
}

const CardCarousel = <T extends { id: string | number }>({
  items,
  renderCard,
  renderSkeleton,
  isLoading = false,
  autoPlayInterval = 5000,
  sliderNavClass = 'bottom-4',
  className = '',
}: CardCarouselProps<T>) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  const swipeHandlers = useSwipe({
    onSwipeLeft: goToNext,
    onSwipeRight: goToPrevious,
  });

  // Reset index when items change
  useEffect(() => {
    setCurrentIndex(0);
  }, [items]);

  // Auto-play
  useEffect(() => {
    if (isLoading || items.length === 0) return;

    const timeout = setTimeout(goToNext, autoPlayInterval);
    return () => clearTimeout(timeout);
  }, [currentIndex, isLoading, items.length, goToNext, autoPlayInterval]);

  if (isLoading && renderSkeleton) {
    return <>{renderSkeleton()}</>;
  }

  if (!items.length) return null;

  return (
    <div className={`relative ${className}`}>
      {/* Cards */}
      <div
        className="transition-all delay-200 duration-500"
        onTouchStart={swipeHandlers.onTouchStart}
        onTouchMove={swipeHandlers.onTouchMove}
        onTouchEnd={swipeHandlers.onTouchEnd}
      >
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`${index === currentIndex ? 'block' : 'hidden'} animate-fadeIn`}
          >
            {renderCard(item, index)}
          </div>
        ))}
      </div>

      {/* Dot Navigation */}
      {items.length > 1 && (
        <ul
          className={`${sliderNavClass} left-0 right-0 z-10 flex justify-center`}
        >
          {items.map((_, index) => (
            <li
              key={`dot-${index}`}
              className="mx-1.5 flex cursor-pointer items-center"
              onClick={() => setCurrentIndex(index)}
            >
              {index === currentIndex ? (
                <IconPlaceholder className="brightness-1 invert" />
              ) : (
                <Dot className="brightness-50 invert" />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CardCarousel;

